/**
 * Shopper identity (the BFF's session cookie) bridged to Loot OS sessions: the
 * context <ShopperProvider> fills, and the hooks pages read it through. All Loot
 * traffic flows through the BFF proxy (same-origin /api).
 */
import type { LootClient } from "@loot-os/client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { flushAnalytics, track } from "./analytics.js";
import { deviceSessionHeaders } from "./deviceSession.js";
import { balanceQueryOptions, lootClient, qk, type ShopperUser } from "./queries.js";
import { queryClient } from "./queryClient.js";
import { useTenant } from "./tenant.js";

/** Identity the provider publishes; everything else lives in the query cache. */
export interface ShopperContextValue {
  /** Shopper from the BFF session (null while signed out or loading). */
  user: ShopperUser | null;
  /** True until the initial session check resolves. */
  authPending: boolean;
  /** True while a signed-in shopper's session token is still being minted (before it is attached). */
  sessionPending: boolean;
  /** Current Loot session token (null while signed out / not yet minted). */
  sessionToken: string | null;
  /** Shared Loot client; session-aware once a token is minted. */
  client: LootClient;
}

/** Written by <ShopperProvider>; read through the hooks below. */
export const ShopperContext = createContext<ShopperContextValue | null>(null);

/** The signed-in shopper's identity; throws outside the provider. */
export function useShopper(): ShopperContextValue {
  const value = useContext(ShopperContext);
  if (!value) throw new Error("useShopper must be used inside <ShopperProvider>");
  return value;
}

/** Balance (integer US cents) for the signed-in shopper; undefined data while signed out. */
export function useBalance() {
  const { sessionToken } = useShopper();
  return useQuery({ ...balanceQueryOptions(), enabled: !!sessionToken });
}

/** Throws the BFF's error slug ("account_locked", "not_enabled", …) or the fallback. */
async function throwBffError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => undefined)) as { error?: string } | undefined;
  throw new Error(body?.error ?? fallback);
}

/** Starts a Stripe checkout for a pack and redirects the browser to it. */
export function useStartCheckout() {
  // Screening stores attach the SEON device session, so a decline never reaches Stripe.
  const screening = useTenant().config.fraudScreening;
  return useMutation({
    mutationFn: async (packId: string) => {
      const res = await fetch("/api/loot/balance/checkout", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(await deviceSessionHeaders(screening)),
        },
        body: JSON.stringify({ packId }),
      });
      if (!res.ok) await throwBffError(res, "checkout failed");
      const body = (await res.json()) as { url: string };
      // Recorded and flushed before the browser leaves; keepalive carries the request through.
      track("checkout_started", { packId });
      void flushAnalytics();
      window.location.assign(body.url);
    },
  });
}

/**
 * Starts (or resumes) identity verification and opens Veriff's hosted flow in a new tab. The tab
 * is opened synchronously on the click and pointed at the URL once it arrives, so pop-up blockers
 * see a user gesture (boxed opens after the await and has to tell shoppers to allow pop-ups).
 * The outcome lands on GET /v1/me later, via Veriff's webhooks.
 */
export function useStartKyc() {
  const { client } = useShopper();
  return useMutation({
    mutationFn: async () => {
      const tab = window.open("", "_blank");
      try {
        const session = await client.startKycSession({
          callbackUrl: `${window.location.origin}/account`,
        });
        if (tab) {
          tab.opener = null;
          tab.location.href = session.url;
        } else {
          window.open(session.url, "_blank", "noopener,noreferrer");
        }
        return session;
      } catch (err) {
        tab?.close();
        throw err;
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.lootMe }),
  });
}

/** Signs the shopper out of the BFF session and clears their cached data. */
export function useSignOut() {
  return useMutation({
    mutationFn: async () => {
      await fetch("/api/auth/signout", { method: "POST" });
    },
    onSuccess: () => {
      // What was queued under the session leaves with its bearer before the token goes.
      void flushAnalytics();
      // Seed the signed-out state directly (an invalidate would just 401).
      queryClient.setQueryData(qk.authMe, null);
      lootClient.setSessionToken(null);
      queryClient.removeQueries({ queryKey: qk.private });
    },
  });
}
